import React, { useEffect, useState } from 'react';
import { useRegisterMutation, useLoginDemoUserMutation } from '@/store/api/authApi';
import { useForm } from 'react-hook-form';
import { FloatingInput } from '@/components/common/FloatingInput';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Cookies from 'js-cookie';
import { registerSchema } from '@/utils/validation';
import toast from 'react-hot-toast';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useNavigate, useSearchParams } from 'react-router-dom';

type RegisterFormData = z.infer<typeof registerSchema>;

// ✅ Reusable Custom Checkbox
const CustomCheckbox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: React.ReactNode;
}) => {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none" onClick={onChange}>
      <div className="w-5 h-5 mt-1 rounded-full border border-gray-400 flex items-center justify-center">
        {checked && <div className="w-5 h-5 bg-yellow-400 rounded-full" />}
      </div>
      <span className="text-xs text-gray-300">{label}</span>
    </label>
  );
};

export const RegisterForm = ({
  onSwitch,
  onClose,
}: {
  onSwitch: (value: string) => void;
  onClose?: () => void;
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const [showReferral, setShowReferral] = useState(false);

  const ageChecked = watch('ageConfirm');

  const [registerMutation, { isLoading }] = useRegisterMutation();
  const [loginDemoUser] = useLoginDemoUserMutation();
  const { whatsappSupport } = useUserInfo();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const refCode = searchParams.get('refCode');

    if (refCode) {
      setValue('referralCode', refCode);
      setShowReferral(true);
    }
  }, [searchParams, setValue]);

  // Submit
  const onSubmit = async (data: RegisterFormData) => {
    try {
      const res = await registerMutation({
        username: data.username,
        mobile: data.mobile,
        password: data.password,
        referralCode: data.referralCode,
      }).unwrap();

      if (res?.accessToken) {
        Cookies.set('access-token', res.accessToken, { expires: 1 });
        toast.success('Registration successful!');
        onClose?.();
      }
    } catch (err: any) {
      toast.error(err?.data?.message || 'Registration failed!');
      console.error('Register failed:', err);
    }
  };

  // Demo login
  const handleDemoLogin = async () => {
    try {
      const res = await loginDemoUser().unwrap();
      if (res?.accessToken) {
        Cookies.set('access-token', res.accessToken, { expires: 1 });
        toast.success('Demo login successful!');
        onClose?.();
      }
    } catch (err) {
      console.error('Demo login failed:', err);
    }
  };

  return (
    <div className="px-4 py-10 mx-auto text-white">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <img src="/logo.png" alt="logo" className="w-[130px]" />
      </div>

      {/* WhatsApp */}
      <a
        href={whatsappSupport}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center w-full bg-green-500 py-2 rounded-md font-semibold mb-6"
      >
        WHATSAPP NOW
      </a>

      <form autoComplete="off" className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <FloatingInput label="Username" register={register('username')} error={errors.username} />

        <FloatingInput label="Mobile Number" register={register('mobile')} error={errors.mobile} />

        <FloatingInput
          label="Password"
          type="password"
          register={register('password')}
          error={errors.password}
        />

        {/* ✅ Referral Toggle */}
        <CustomCheckbox
          checked={showReferral}
          onChange={() => setShowReferral((prev) => !prev)}
          label="Referral Code (Optional)"
        />

        {showReferral && (
          <FloatingInput
            label="Enter Referral Code"
            register={register('referralCode')}
            error={errors.referralCode}
          />
        )}

        {/* ✅ Age Confirm */}
        <CustomCheckbox
          checked={!!ageChecked}
          onChange={() => setValue('ageConfirm', !ageChecked)}
          label={
            <>
              We support safe and responsible gambling <br />
              Please confirm you are 18 years of age
            </>
          }
        />

        {errors.ageConfirm && (
          <p className="text-red-500 text-xs mt-1">{errors.ageConfirm.message}</p>
        )}

        {/* Register */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary text-black font-semibold py-2 rounded-md disabled:opacity-60"
        >
          {isLoading ? 'Registering...' : 'REGISTER'}
        </button>

        {/* Demo Login */}
        <button
          type="button"
          onClick={handleDemoLogin}
          className="w-full bg-primary text-black font-semibold py-2 rounded-md"
        >
          LOGIN WITH DEMO ID
        </button>

        {/* Switch */}
        <div className="text-center text-xs text-gray-400">
          Already have an account?{' '}
          <button onClick={() => onSwitch('login')} className="underline">
            LOGIN
          </button>
        </div>
      </form>
    </div>
  );
};
